// TODO update this to import from commons
// import { gql } from '@reverbdotcom/commons/src/gql';
// eslint-disable-next-line no-restricted-imports
import { gql } from '@apollo/client';
import React from 'react';
import {
  CoreProductReviews,
  reverb_search_ProductReviewsSearchRequest_Rating,
  reverb_search_ProductReviewsSearchRequest_Sort,
} from '@reverbdotcom/commons/src/gql/graphql';
import { ProductReviewCardFragment } from './ProductReviewCard';
import RQLPagination from '@reverbdotcom/commons/src/components/rql_pagination';
import { getOffsetFromPage } from '../pagination_helpers';
import { ProductReviewsFilterRow } from './ProductReviewsFilterRow';
import { ProductReviewFilterFragment } from './ProductReviewFilter';
import { ProductReviewsHeader } from './ProductReviewsHeader';
import I18n from 'i18n-js';
import { ProductReviews } from './ProductReviews';
import { RCSearchInput, RCSkeleton } from '@reverbdotcom/cadence/components';
import { updateParams } from '@reverbdotcom/commons';
import { UpdateURLProvider, UpdateUrlArgs } from '@reverbdotcom/commons/src/components/with_update_url';
import { omit, times } from 'lodash';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { ProductReviewInteraction } from '@reverbdotcom/commons/src/elog/mparticle_types';
import RouterHistory from '@reverbdotcom/commons/src/cms/lib/router_history';
import { useURL } from '@reverbdotcom/commons/src/url_context';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { Location } from 'history';

interface Props {
  cspId: string;
  trackingDisplayStyle: 'product' | 'csp';
  location: Location;
  ssr?: boolean;
}

const RATINGS = {
  '5': reverb_search_ProductReviewsSearchRequest_Rating.FIVE,
  '4': reverb_search_ProductReviewsSearchRequest_Rating.FOUR,
  '3': reverb_search_ProductReviewsSearchRequest_Rating.THREE,
  '2': reverb_search_ProductReviewsSearchRequest_Rating.TWO,
  '1': reverb_search_ProductReviewsSearchRequest_Rating.ONE,
};

const SORT = {
  'created_at|desc': reverb_search_ProductReviewsSearchRequest_Sort.CREATED_AT_DESC,
  'product_review_votes_count|desc': reverb_search_ProductReviewsSearchRequest_Sort.VOTES_DESC,
  'rating|desc': reverb_search_ProductReviewsSearchRequest_Sort.RATING_DESC,
  'rating|asc': reverb_search_ProductReviewsSearchRequest_Sort.RATING_ASC,
  'updated_at|desc': reverb_search_ProductReviewsSearchRequest_Sort.UPDATED_AT_DESC,
};

const LIMIT = 5;
const COMPONENT_NAME = 'ProductReviewsLayout';

function ProductReviewSummary({ props, data, loading }) {
  const { location, trackingDisplayStyle, cspId } = props;
  const [searchDisplayValue, setSearchDisplayValue] = React.useState(location?.query?.review_query || '');
  const url = useURL();

  function refetchQuery() {
    const query = {
      ...location.query,
      review_query: searchDisplayValue,
    };

    trackEvent({
      eventName: MParticleEventName.ProductReviewAction,
      productReviewInteraction: ProductReviewInteraction.SearchReviews,
      displayStyle: trackingDisplayStyle,
      cspId: cspId,
      query: searchDisplayValue,
    });

    RouterHistory.push({
      query,
      pathname: location.pathname,
      hash: url.hash,
    });
  }

  function clearQuery() {
    trackEvent({
      eventName: MParticleEventName.ProductReviewAction,
      productReviewInteraction: ProductReviewInteraction.ClearSearchQuery,
      displayStyle: trackingDisplayStyle,
      cspId: cspId,
      query: searchDisplayValue,
    });

    setSearchDisplayValue('');
    if (location.query) {
      const query = omit(location.query, 'review_query');

      RouterHistory.push({
        query,
        pathname: location.pathname,
        hash: url.hash,
      });
    }
  }

  return (
    <>
      <ProductReviewsHeader
        stats={data?.csp?.statsSearch?.stats}
        csp={data?.csp}
        trackingDisplayStyle={trackingDisplayStyle}
      />
      <ProductReviewsFilterRow
        loading={loading}
        reviewCount={data?.csp?.statsSearch?.stats?.total}
        filters={data?.csp?.productReviewSearch?.filters}
        searchInput={
          <div className="width-rem-30 mobile-fx-width-100">
            <RCSearchInput
              value={searchDisplayValue}
              inputId="product-reviews-search"
              onChange={(e) => setSearchDisplayValue(e.target.value)}
              onSubmit={refetchQuery}
              onClear={clearQuery}
              placeholder={I18n.t('discovery.productReviews.searchInput.placeholder')}
            />
          </div>
        }
      />
    </>
  );
}

function ProductReviewContent({ props, data, loading }) {
  const { location, trackingDisplayStyle } = props;

  return (
    <>
      {loading ? (
        times(6, (_) => (<RCSkeleton height="15rem" display="block" />))
      ) : (
        <ProductReviews
          reviews={data?.csp?.productReviewSearch?.productReviews}
          reviewCount={data?.csp?.statsSearch?.stats?.total}
          cspId={data?.csp?.id}
          searchQuery={location?.query?.review_query}
          trackingDisplayStyle={trackingDisplayStyle}
        />
      )}

      <RQLPagination
        limit={LIMIT}
        total={data?.csp?.productReviewSearch?.total}
        anchor="reviews-section"
        offset={getOffsetFromPage(location?.query?.product_review_page, LIMIT)}
        pageParam="product_review_page"
        replaceHistory
        trackingData={{ displayStyle: COMPONENT_NAME }}
      />
    </>
  );
}

export function ProductReviewsLayout(props: Props) {
  const { location, cspId, ssr } = props;
  const { data, loading } = useQuery<CoreProductReviews.Query, CoreProductReviews.Variables>(
    PRODUCT_REVIEWS_LAYOUT,
    {
      skip: !cspId,
      ssr: !!ssr,
      variables: {
        cspId: cspId,
        offset: getOffsetFromPage(location?.query?.product_review_page, LIMIT),
        ratings: RATINGS[location?.query?.rating],
        verified: location?.query?.verified === 'true',
        sort: SORT[location?.query?.review_sort],
        fullTextQuery: location?.query?.review_query,
      },
    },
  );

  function updateUrl(args: UpdateUrlArgs) {
    const query = updateParams(location.query, args.newParams);

    return {
      pathname: args.location.pathname,
      query,
    };
  }

  if (!loading && !data?.csp) return null;

  return (
    <UpdateURLProvider updateUrl={updateUrl}>
      <section
        id="reviews-section" // needed for the CSP page jumplinks
        className="scaling-mtb-8 d-flex fx-dir-col gap-4"
      >
        <h1 className="size-250 weight-bold mb-2">
          {I18n.t('discovery.productReviews.header.title')}
        </h1>
        <ProductReviewSummary
          props={props}
          data={data}
          loading={loading}
        />
        <ProductReviewContent
          props={props}
          data={data}
          loading={loading}
        />
      </section>
    </UpdateURLProvider>
  );
}

export const ProductReviewCSPFragment = gql`
  fragment ProductReviewCSPFields on CSP {
    _id
    id
    slug
    title
    myReview {
      id
      body
      title
      rating
    }
  }
`;

const PRODUCT_REVIEWS_LAYOUT = gql`
  query Core_Product_Reviews(
    $cspId: String
    $offset: Int
    $ratings: [reverb_search_ProductReviewsSearchRequest_Rating]
    $verified: Boolean
    $sort: reverb_search_ProductReviewsSearchRequest_Sort
    $fullTextQuery: String
  ) {
    csp(
      input: {
        id: $cspId
      }
    ) {
      _id
      ...ProductReviewCSPFields
      productReviewSearch(
        input: {
          sort: $sort
          ratings: $ratings
          verified: $verified
          limit: 5
          offset: $offset
          fullTextQuery: $fullTextQuery
        }
      ) {
        total
        productReviews {
          _id
          ...ProductReviewCardFields
        }
        filters {
          ...ProductReviewFilterFields
        }
      }
      statsSearch: productReviewSearch(
        input: {
          limit: 0
        }
      ) {
        stats {
          total
          averageRating
          ratingsDistribution {
            rating
            reviewCount
          }
        }
      }
    }
  }
  ${ProductReviewCardFragment}
  ${ProductReviewFilterFragment}
  ${ProductReviewCSPFragment}
`;
